import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

export type AiAgentStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type AiAgentType = 'CONVERSATIONAL' | 'TASK' | 'WORKFLOW';
export type AgentSessionStatus = 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'ABANDONED';
export type AgentMemoryType = 'FACT' | 'PREFERENCE' | 'INSTRUCTION' | 'CONTEXT';

export interface LeadScoringCriterion {
  name: string;
  weight: number;
  description: string;
}

export interface LeadScoringConfig {
  criteria: LeadScoringCriterion[];
}

export type ExitConditionType = 'message_signal' | 'lead_score';
export type ExitOperator = 'gt' | 'lt' | 'eq' | 'gte' | 'lte';

export interface ExitCondition {
  id: string;
  name: string;
  type: ExitConditionType;
  condition?: string;
  operator?: ExitOperator;
  value?: number;
}

export interface AiAgent {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  status: AiAgentStatus;
  type: AiAgentType;
  llmProvider: string;
  llmModel: string;
  systemPrompt?: string;
  temperature: number;
  maxTokens: number;
  enabledTools: string[];
  toolConfig?: Record<string, any>;
  knowledgeBaseId?: string;
  introMessage?: string;
  deploymentStatus?: string;
  showToOperator: boolean;
  autoReply: boolean;
  confidenceThreshold: number;
  templateId?: string;
  leadScoringConfig?: LeadScoringConfig;
  exitConditions?: ExitCondition[];
  createdAt: string;
  updatedAt: string;
  _count?: { sessions: number };
  sessionCount?: number;
}

export interface AgentInstruction {
  id: string;
  agentId: string;
  title: string;
  content: string;
  isActive: boolean;
  priority: number;
  createdAt: string;
}

export interface AgentMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  toolName?: string;
  toolInput?: any;
  toolOutput?: any;
  createdAt: string;
}

export interface AgentSession {
  id: string;
  agentId: string;
  status: AgentSessionStatus;
  turnCount: number;
  pendingQuestion?: string;
  inputTokens: number;
  outputTokens: number;
  startedAt: string;
  endedAt?: string;
  messages?: AgentMessage[];
}

export interface AgentMemory {
  id: string;
  type: AgentMemoryType;
  content: string;
  sourceType?: string;
  importance: number;
  expiresAt?: string;
  createdAt: string;
}

export interface CreateAgentDto {
  name: string;
  description?: string;
  type?: AiAgentType;
  systemPrompt?: string;
  llmModel?: string;
  temperature?: number;
  maxTokens?: number;
  enabledTools?: string[];
  autoReply?: boolean;
  confidenceThreshold?: number;
  templateId?: string;
  introMessage?: string;
  leadScoringConfig?: LeadScoringConfig;
  exitConditions?: ExitCondition[];
}

export interface AgentTemplate {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  icon: string;
  badge: string | null;
  systemPrompt: string;
  llmModel: string;
  temperature: number;
  enabledTools: string[];
  defaultInstructions: { title: string; content: string; priority: number }[];
  defaultKbSources: string[];
  isBuiltIn: boolean;
  isActive: boolean;
}

export interface ToolSchema {
  name: string;
  description: string;
  parameters: Record<string, any>;
}

export interface AgentKnowledgeSource {
  id: string;
  agentId: string;
  knowledgeBaseId: string;
  isActive: boolean;
  knowledgeBase?: { id: string; name: string; description?: string; isActive: boolean };
}

export interface ToolCall {
  name: string;
  params: Record<string, unknown>;
  result?: unknown;
}

export interface AgentGuardrail {
  name: string;
  observeFor: string;
  howToReact: string;
}

export interface PerformanceTestResult {
  testRunId: string;
  agentId: string;
  status: 'COMPLETED' | 'FAILED';
  runAt: string;
  results: Array<{
    question: string;
    expectedAnswer: string;
    actualAnswer: string;
    score: number;
    label: 'Excellent' | 'Good' | 'Needs Work' | 'Poor';
  }>;
}

export interface ChatResponse {
  reply: string;
  needsHumanInput: boolean;
  pendingQuestion?: string;
  tokenUsage: { input: number; output: number };
  toolCalls?: ToolCall[];
}

export interface AgentDashboardAnalytics {
  totalSessions: number;
  activeSessions: number;
  resolutionRate: number;
  avgResponseTime: number;
  avgSessionDuration: number;
  avgTurnsPerSession: number;
  totalTokensUsed: number;
  avgCsatScore: number | null;
  topExitReasons: Array<{ reason: string; count: number }>;
  dailyStats: Array<{ date: string; sessions: number; resolutions: number; handoffs: number }>;
  agentStats: Array<{ agentId: string; agentName: string; sessions: number; resolutionRate: number; avgCsat: number | null }>;
}

export interface ListAgentsParams {
  search?: string;
  type?: string;
  status?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  page?: number;
  limit?: number;
  [key: string]: any;
}

export const agentApi = createApi({
  reducerPath: 'agentApi',
  baseQuery,
  tagTypes: ['Agent', 'Instruction', 'Memory', 'Template', 'KnowledgeSource', 'ToolSchema', 'Guardrail'],
  endpoints: (builder) => ({
    listAgents: builder.query<{ data: AiAgent[]; total: number; page?: number; limit?: number }, ListAgentsParams | void>({
      query: (filters = {}) => {
        const params: Record<string, any> = {};
        if (filters?.search) params.search = filters.search;
        else if (filters?.globalSearch) params.search = filters.globalSearch;
        if (filters?.type) params.type = filters.type;
        if (filters?.status) params.status = filters.status;
        if (filters?.llmModel) params.llmModel = filters.llmModel;
        if (filters?.createdFrom) params.createdFrom = filters.createdFrom;
        if (filters?.createdTo) params.createdTo = filters.createdTo;
        if (filters?.updatedFrom) params.updatedFrom = filters.updatedFrom;
        if (filters?.updatedTo) params.updatedTo = filters.updatedTo;
        params.page = filters?.page ?? 1;
        params.limit = filters?.limit ?? filters?.pageSize ?? 20;

        return {
          url: 'api/ai-agents',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: any) => {
        const payload = response?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.list)
              ? payload.list
              : Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.list)
                  ? response.list
                  : [];
        const total = payload?.total ?? response?.total ?? response?.data?.total ?? list.length;
        const page = payload?.page ?? response?.page ?? 1;
        const limit = payload?.limit ?? response?.limit ?? 20;

        return {
          data: list,
          total,
          page,
          limit,
        };
      },
      providesTags: ['Agent'],
    }),
    getAgent: builder.query<{ data: AiAgent }, string>({
      query: (agentId) => `api/ai-agents/${agentId}`,
      providesTags: (_r, _e, id) => [{ type: 'Agent', id }],
    }),
    createAgent: builder.mutation<{ data: AiAgent }, CreateAgentDto>({
      query: (body) => ({ url: 'api/ai-agents', method: 'POST', body }),
      invalidatesTags: ['Agent'],
    }),
    updateAgent: builder.mutation<{ data: AiAgent }, { agentId: string } & Partial<CreateAgentDto> & { status?: AiAgentStatus }>({
      query: ({ agentId, ...body }) => ({ url: `api/ai-agents/${agentId}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { agentId }) => ['Agent', { type: 'Agent', id: agentId }],
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (agentId) => ({ url: `api/ai-agents/${agentId}`, method: 'DELETE' }),
      invalidatesTags: ['Agent'],
    }),

    // Instructions
    listInstructions: builder.query<{ data: AgentInstruction[] }, string>({
      query: (agentId) => `api/ai-agents/${agentId}/instructions`,
      providesTags: ['Instruction'],
    }),
    addInstruction: builder.mutation<{ data: AgentInstruction }, { agentId: string; title: string; content: string; priority?: number }>({
      query: ({ agentId, ...body }) => ({ url: `api/ai-agents/${agentId}/instructions`, method: 'POST', body }),
      invalidatesTags: ['Instruction'],
    }),
    updateInstruction: builder.mutation<{ data: AgentInstruction }, { agentId: string; instructionId: string; isActive?: boolean; priority?: number; title?: string; content?: string }>({
      query: ({ agentId, instructionId, ...body }) => ({ url: `api/ai-agents/${agentId}/instructions/${instructionId}`, method: 'PATCH', body }),
      invalidatesTags: ['Instruction'],
    }),
    deleteInstruction: builder.mutation<void, { agentId: string; instructionId: string }>({
      query: ({ agentId, instructionId }) => ({ url: `api/ai-agents/${agentId}/instructions/${instructionId}`, method: 'DELETE' }),
      invalidatesTags: ['Instruction'],
    }),

    // Sessions
    startSession: builder.mutation<{ data: AgentSession }, { agentId: string; conversationId?: string; operatorUserId?: string; contactId?: string }>({
      query: (body) => ({ url: 'api/ai-agents/sessions', method: 'POST', body }),
    }),
    getSession: builder.query<{ data: AgentSession }, string>({
      query: (sessionId) => `api/ai-agents/sessions/${sessionId}`,
    }),
    chat: builder.mutation<{ data: ChatResponse }, { sessionId: string; content: string }>({
      query: ({ sessionId, content }) => ({ url: `api/ai-agents/sessions/${sessionId}/chat`, method: 'POST', body: { content } }),
    }),
    resumeSession: builder.mutation<{ data: ChatResponse }, { sessionId: string; content: string }>({
      query: ({ sessionId, content }) => ({ url: `api/ai-agents/sessions/${sessionId}/resume`, method: 'POST', body: { content } }),
    }),
    endSession: builder.mutation<void, string>({
      query: (sessionId) => ({ url: `api/ai-agents/sessions/${sessionId}/end`, method: 'POST' }),
    }),
    submitFeedback: builder.mutation<void, { sessionId: string; rating: number; comment?: string }>({
      query: ({ sessionId, ...body }) => ({ url: `api/ai-agents/sessions/${sessionId}/feedback`, method: 'POST', body }),
    }),

    // Memories
    listMemories: builder.query<{ data: AgentMemory[] }, string>({
      query: (agentId) => `api/ai-agents/${agentId}/memories`,
      providesTags: ['Memory'],
    }),
    listMemoriesPaginated: builder.query<
      { data: { list: AgentMemory[]; total: number } },
      { agentId: string; type?: string; page?: number; limit?: number }
    >({
      query: ({ agentId, ...params }) => ({
        url: `api/ai-agents/${agentId}/memories`,
        params,
      }),
      providesTags: ['Memory'],
    }),
    updateMemory: builder.mutation<{ data: AgentMemory }, { memoryId: string; status?: string; importance?: number }>({
      query: ({ memoryId, ...body }) => ({ url: `api/ai-agents/memories/${memoryId}`, method: 'PATCH', body }),
      invalidatesTags: ['Memory'],
    }),
    deleteMemory: builder.mutation<void, string>({
      query: (memoryId) => ({ url: `api/ai-agents/memories/${memoryId}`, method: 'DELETE' }),
      invalidatesTags: ['Memory'],
    }),

    // Templates
    listTemplates: builder.query<{ data: AgentTemplate[] }, void>({
      query: () => 'api/ai-agents/templates',
      providesTags: ['Template'],
    }),
    getTemplate: builder.query<{ data: AgentTemplate }, string>({
      query: (id) => `api/ai-agents/templates/${id}`,
    }),

    // Available Actions / Tools
    listAvailableActions: builder.query<{ data: ToolSchema[] }, void>({
      query: () => 'api/ai-agents/actions/available',
      providesTags: ['ToolSchema'],
    }),
    updateAgentActions: builder.mutation<{ data: AiAgent }, { agentId: string; enabledTools: string[] }>({
      query: ({ agentId, enabledTools }) => ({ url: `api/ai-agents/${agentId}/actions`, method: 'PATCH', body: { enabledTools } }),
      invalidatesTags: (_r, _e, { agentId }) => ['Agent', { type: 'Agent', id: agentId }],
    }),
    updateAgentToolConfig: builder.mutation<{ data: AiAgent }, { agentId: string; toolName: string; config: Record<string, any> }>({
      query: ({ agentId, toolName, config }) => ({
        url: `api/ai-agents/${agentId}/tool-config`,
        method: 'PATCH',
        body: { toolName, config },
      }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'Agent', id: agentId }],
    }),

    // Knowledge Sources
    linkKnowledgeSource: builder.mutation<{ data: AgentKnowledgeSource }, { agentId: string; knowledgeBaseId: string }>({
      query: ({ agentId, knowledgeBaseId }) => ({ url: `api/ai-agents/${agentId}/knowledge-sources`, method: 'POST', body: { knowledgeBaseId } }),
      invalidatesTags: ['KnowledgeSource'],
    }),
    listKnowledgeSources: builder.query<{ data: AgentKnowledgeSource[] }, string>({
      query: (agentId) => `api/ai-agents/${agentId}/knowledge-sources`,
      providesTags: ['KnowledgeSource'],
    }),
    unlinkKnowledgeSource: builder.mutation<void, { agentId: string; sourceId: string }>({
      query: ({ agentId, sourceId }) => ({ url: `api/ai-agents/${agentId}/knowledge-sources/${sourceId}`, method: 'DELETE' }),
      invalidatesTags: ['KnowledgeSource'],
    }),

    // Knowledge Base Document Ingestion
    uploadKBDocument: builder.mutation<{ data: any }, FormData>({
      query: (formData) => ({
        url: 'api/knowledge-base/upload',
        method: 'POST',
        body: formData,
        formData: true,
      }),
    }),
    indexKBUrl: builder.mutation<{ data: any }, { url: string; crawlDepth?: number }>({
      query: (body) => ({ url: 'api/knowledge-base/url', method: 'POST', body }),
    }),
    createKBQAPair: builder.mutation<{ data: any }, { question: string; answer: string }>({
      query: (body) => ({ url: 'api/knowledge-base/qa', method: 'POST', body }),
    }),

    // CSAT
    submitCsat: builder.mutation<{ data: any }, { sessionId: string; rating: number; comment?: string }>({
      query: ({ sessionId, ...body }) => ({ url: `api/ai-agents/sessions/${sessionId}/csat`, method: 'POST', body }),
    }),

    // Analytics Dashboard
    getDashboardAnalytics: builder.query<{ data: AgentDashboardAnalytics }, Record<string, any> | void>({
      query: (params) => ({
        url: 'api/ai-agents/analytics/dashboard',
        params: params || {},
      }),
      transformResponse: (response: any) => {
        const payload = response?.data?.data ?? response?.data ?? response;
        return {
          data: payload,
        };
      },
      providesTags: ['Agent'],
    }),

    // Deployment
    deployAgent: builder.mutation<{ data: any }, { agentId: string; channels?: string[]; trigger?: string; keywords?: string[] }>({
      query: ({ agentId, ...body }) => ({ url: `api/ai-agents/${agentId}/deploy`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { agentId }) => ['Agent', { type: 'Agent', id: agentId }],
    }),
    undeployAgent: builder.mutation<void, string>({
      query: (agentId) => ({ url: `api/ai-agents/${agentId}/deploy`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, agentId) => ['Agent', { type: 'Agent', id: agentId }],
    }),
    getDeploymentStatus: builder.query<{ data: { id: string; status: string; deploymentStatus: string } }, string>({
      query: (agentId) => `api/ai-agents/${agentId}/deploy`,
    }),

    // Guardrails
    updateGuardrails: builder.mutation<{ data: any }, { agentId: string; guardrails: AgentGuardrail[] }>({
      query: ({ agentId, guardrails }) => ({
        url: `api/ai-agents/${agentId}/guardrails`,
        method: 'PATCH',
        body: { guardrails },
      }),
      invalidatesTags: (_r, _e, { agentId }) => [{ type: 'Agent', id: agentId }],
    }),

    // Performance Testing
    runPerformanceTest: builder.mutation<{ data: PerformanceTestResult }, string>({
      query: (agentId) => ({ url: `api/ai-agents/${agentId}/performance-test`, method: 'POST' }),
    }),
    getPerformanceTestResult: builder.query<{ data: PerformanceTestResult }, { agentId: string; testRunId: string }>({
      query: ({ agentId, testRunId }) => `api/ai-agents/${agentId}/performance-test/${testRunId}`,
    }),
  }),
});

export const {
  useListAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useListInstructionsQuery,
  useAddInstructionMutation,
  useUpdateInstructionMutation,
  useDeleteInstructionMutation,
  useStartSessionMutation,
  useGetSessionQuery,
  useChatMutation,
  useResumeSessionMutation,
  useEndSessionMutation,
  useSubmitFeedbackMutation,
  useListMemoriesQuery,
  useListMemoriesPaginatedQuery,
  useUpdateMemoryMutation,
  useDeleteMemoryMutation,
  // Templates
  useListTemplatesQuery,
  useGetTemplateQuery,
  // Actions / Tools
  useListAvailableActionsQuery,
  useUpdateAgentActionsMutation,
  useUpdateAgentToolConfigMutation,
  // Knowledge Sources
  useLinkKnowledgeSourceMutation,
  useListKnowledgeSourcesQuery,
  useUnlinkKnowledgeSourceMutation,
  // KB Document Ingestion
  useUploadKBDocumentMutation,
  useIndexKBUrlMutation,
  useCreateKBQAPairMutation,
  // CSAT
  useSubmitCsatMutation,
  // Analytics
  useGetDashboardAnalyticsQuery,
  // Deployment
  useDeployAgentMutation,
  useUndeployAgentMutation,
  useGetDeploymentStatusQuery,
  // Guardrails
  useUpdateGuardrailsMutation,
  // Performance Testing
  useRunPerformanceTestMutation,
  useGetPerformanceTestResultQuery,
} = agentApi;
